import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Users,
  Monitor,
  Building2,
} from "lucide-react";

const workshops = [
  {
    title: "3-Hour AI Essentials Session",
    duration: "3 hours",
    deliveryFormat: "In-person or virtual",
    bestFor: "Individuals, professionals, managers, and business owners who want a strong practical introduction to AI",
    covers: [
      "What AI is and why it matters now",
      "Key AI tools and what they do",
      "Practical ways AI can help in everyday work",
      "How to write better prompts",
      "Safe and responsible use of AI",
      "Real examples for work and business",
    ],
    outcomes: [
      "Clear understanding of AI and its practical applications",
      "Knowledge of key AI tools and their uses",
      "Improved prompt writing skills",
      "Awareness of safe and responsible AI use",
      "Confidence to start applying AI in real work",
      "Practical ideas for immediate implementation",
    ],
  },
  {
    title: "Full-Day Practical AI Workshop",
    duration: "Full day (6-7 hours)",
    deliveryFormat: "In-person or virtual",
    bestFor: "Professionals, teams, and businesses that want deeper practical learning and hands-on use",
    covers: [
      "AI foundations and core concepts",
      "Practical tools and comprehensive use cases",
      "Prompt writing in depth with advanced techniques",
      "Using AI for productivity, communication, and planning",
      "Business examples and workflow support",
      "Responsible use and common mistakes to avoid",
      "Hands-on exercises with real workplace scenarios",
    ],
    outcomes: [
      "Strong working knowledge of AI fundamentals",
      "Proficiency with multiple AI tools",
      "Advanced prompt engineering skills",
      "Ability to integrate AI into daily workflows",
      "Understanding of AI limitations and best practices",
      "Practical skills ready for immediate application",
    ],
    featured: true,
  },
];

export default function WorkshopsPage() {
  return (
    <div data-testid="workshops-page">
      {/* Hero */}
      <section className="page-hero text-white py-16 md:py-20">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl">
              <h1 className="heading-display text-white mb-5 font-['Manrope']">
                AI Workshops
              </h1>
              <p className="text-lg md:text-xl text-[#94A3B8] leading-relaxed">
                Practical AI workshops designed to help professionals and businesses 
                use AI confidently, safely, and productively.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Workshops */}
      <section className="bg-white section-spacing" data-testid="workshops-list-section">
        <div className="container-main">
          <div className="space-y-10 md:space-y-12">
            {workshops.map((workshop, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div 
                  className={`bg-white rounded-2xl border overflow-hidden ${
                    workshop.featured ? "border-[#008A90] border-2 shadow-lg" : "border-[#E2E8F0]"
                  }`}
                  data-testid={`workshop-detail-${workshop.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {workshop.featured && (
                    <div className="bg-gradient-to-r from-[#008A90] to-[#007075] text-white text-center py-2.5 text-sm font-semibold tracking-wide">
                      Most Comprehensive Option
                    </div>
                  )}
                  <div className="p-6 md:p-8 lg:p-10">
                    <h2 className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-5 font-['Manrope']">
                      {workshop.title}
                    </h2>

                    {/* Meta info */}
                    <div className="flex flex-wrap gap-4 md:gap-6 mb-6">
                      <div className="flex items-center gap-2 text-[#475569]">
                        <Clock size={18} className="text-[#008A90]" />
                        <span className="font-medium text-[15px]">{workshop.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#475569]">
                        <Monitor size={18} className="text-[#008A90]" />
                        <span className="font-medium text-[15px]">{workshop.deliveryFormat}</span>
                      </div>
                    </div>

                    {/* Best for */}
                    <div className="mb-8 p-5 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                      <div className="flex items-start gap-3">
                        <div className="icon-wrapper flex-shrink-0">
                          <Users size={20} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#0A192F] mb-1.5 font-['Manrope']">
                            Best for
                          </h3>
                          <p className="text-body">{workshop.bestFor}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                      {/* What it covers */}
                      <div>
                        <h3 className="text-lg font-semibold text-[#0A192F] mb-4 font-['Manrope']">
                          What it covers
                        </h3>
                        <ul className="space-y-2.5">
                          {workshop.covers.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-body-sm">
                              <CheckCircle2 size={16} className="text-[#008A90] mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Learning outcomes */}
                      <div>
                        <h3 className="text-lg font-semibold text-[#0A192F] mb-4 font-['Manrope']">
                          Learning outcomes
                        </h3>
                        <ul className="space-y-2.5">
                          {workshop.outcomes.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-body-sm">
                              <CheckCircle2 size={16} className="text-[#008A90] mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Pricing note */}
                    <div className="border-t border-[#E2E8F0] pt-6">
                      <p className="text-body-sm italic mb-5">
                        Workshop formats, availability, and pricing are available on enquiry.
                      </p>
                      <Link to="/contact">
                        <Button 
                          className="bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-7 py-5 transition-all hover:-translate-y-0.5"
                          data-testid={`enquire-workshop-btn-${index}`}
                        >
                          Enquire About This Workshop
                          <ArrowRight size={16} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Team Training CTA */}
      <section className="bg-[#F8FAFC] section-spacing" data-testid="team-training-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center">
              <div className="icon-wrapper icon-wrapper-lg mx-auto mb-6">
                <Building2 size={28} />
              </div>
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Looking for team or business training?
              </h2>
              <p className="text-body-lg mb-8">
                We offer customised workshops for teams and organisations. Get in touch 
                to discuss your specific training needs, team size, and objectives.
              </p>
              <Link to="/contact">
                <Button 
                  className="bg-[#008A90] hover:bg-[#007075] text-white font-semibold px-7 py-6 transition-all hover:-translate-y-0.5"
                  data-testid="team-training-enquire-btn"
                >
                  Discuss Team Training
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white section-spacing" data-testid="workshops-final-cta">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Ready to get started?
              </h2>
              <p className="text-body-lg mb-8">
                Get in touch to discuss the right workshop for you, your team, or your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button 
                    className="bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-7 py-6 transition-all hover:-translate-y-0.5"
                    data-testid="workshops-cta-enquire-btn"
                  >
                    Enquire About a Workshop
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold px-7 py-6 transition-all hover:-translate-y-0.5"
                    data-testid="workshops-cta-discovery-btn"
                  >
                    Book a Discovery Call
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
